import type { ITaskGroupModel, ITaskTypeModel } from '@samc/taskmanagement-ui';

export interface ITaskTypePickerGroupRow {
  kind: 'group';
  key: string;
  text: string;
  level: number;
}

export interface ITaskTypePickerTaskTypeRow {
  kind: 'taskType';
  key: string;
  text: string;
  level: number;
  id: number;
}

export type ITaskTypePickerRow = ITaskTypePickerGroupRow | ITaskTypePickerTaskTypeRow;

export function filterTaskTypePickerRowsBySearch(
  rows: ITaskTypePickerRow[],
  search: string
): ITaskTypePickerRow[] {
  const q = search.trim().toLowerCase();
  if (q === '') {
    return rows;
  }
  const keep = new Set<number>();
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (row.kind !== 'taskType') {
      continue;
    }
    const textMatch =
      row.text.toLowerCase().includes(q) || row.key.toLowerCase().includes(q);
    if (!textMatch) {
      continue;
    }
    keep.add(i);
    let minLevel = row.level;
    for (let j = i - 1; j >= 0; j--) {
      const parent = rows[j];
      if (parent.kind !== 'group') {
        continue;
      }
      if (parent.level < minLevel) {
        keep.add(j);
        minLevel = parent.level;
      }
      if (parent.level === 0) {
        break;
      }
    }
  }
  return rows.filter((_, i) => keep.has(i));
}

export function buildTaskTypePickerRows(
  groups: ITaskGroupModel[],
  taskTypes: ITaskTypeModel[]
): ITaskTypePickerRow[] {
  const rows: ITaskTypePickerRow[] = [];
  const typesByGroup = new Map<number, ITaskTypeModel[]>();

  for (const taskType of taskTypes) {
    const existing = typesByGroup.get(taskType.taskGroupId) ?? [];
    existing.push(taskType);
    typesByGroup.set(taskType.taskGroupId, existing);
  }

  const sortedGroups = [...groups].sort((a, b) => a.name.localeCompare(b.name));

  for (const group of sortedGroups) {
    const groupTaskTypes = (typesByGroup.get(group.id) ?? []).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    if (groupTaskTypes.length === 0) {
      continue;
    }

    rows.push({
      kind: 'group',
      key: String(group.id),
      text: group.name,
      level: 0,
    });

    for (const taskType of groupTaskTypes) {
      rows.push({
        kind: 'taskType',
        key: String(taskType.id),
        text: taskType.name,
        level: 1,
        id: taskType.id,
      });
    }
  }

  return rows;
}
